import React, { useState, useEffect, useRef, type JSX } from "react";
import { useStreamingMessage } from "../../hooks/useStreamingMessage";
import { renderBotMessageWithFacts } from "./botMessageRenderer";

/** Match the CSS fade-in duration below. */
const FADE_IN_MS = 600;

const FADE_IN_STYLES = `
        .streaming-fade-in {
          animation: streamingFadeIn 0.6s ease-out;
        }
        @keyframes streamingFadeIn {
          from {
            opacity: 0.6;
          }
          to {
            opacity: 1;
          }
        }
      `;

interface BotMessageProps {
  message: any;
  allFacts: any[];
  allDocuments: any[];
}

/**
 * Phase 7: Wrapper component for bot messages that uses the new streaming hook
 * This component calls useStreamingMessage for a single message
 */
export default function BotMessageContent({
  message,
  allFacts,
  allDocuments,
}: BotMessageProps): JSX.Element {
  // Phase 8: Use the new streaming hook to get displayedText
  // Only use streaming system for messages with a turnId
  const streamingData = useStreamingMessage(message.turnId || "");

  // Track when streaming just completed (for fade-in animation)
  const [justCompleted, setJustCompleted] = useState(false);
  const prevIsComplete = useRef(streamingData.isComplete);

  useEffect(() => {
    // Detect transition from not-complete to complete (stream finished)
    if (streamingData.isComplete && !prevIsComplete.current) {
      setJustCompleted(true);
      // Clear the flag after animation completes (match CSS duration)
      const timer = setTimeout(() => setJustCompleted(false), FADE_IN_MS);
      return () => clearTimeout(timer);
    }
    prevIsComplete.current = streamingData.isComplete;
  }, [streamingData.isComplete]);

  // If message has no turnId, just use message.text directly (welcome messages, etc.)
  if (!message.turnId) {
    return <>{renderBotMessageWithFacts(allFacts, allDocuments, message.text || "")}</>;
  }

  // For messages with turnId, prefer the streamed text
  const text = streamingData.displayedText || message.text || "";

  return (
    <span className={justCompleted ? "streaming-fade-in" : ""}>
      {renderBotMessageWithFacts(allFacts, allDocuments, text)}
      <style>{FADE_IN_STYLES}</style>
    </span>
  );
}
